import { TokenUtils } from "./tokenUtils";
import SecureStorage from "./secureStorage";
import Logger from "./logger";
import { RoleTypes } from "../../types";

/**
 * Secure role validation utility
 */
export class RoleValidator {
  private static roleCache: Map<string, { role: RoleTypes; expires: number }> =
    new Map();

  /**
   * Get user role from JWT token
   */
  static getRoleFromToken(): RoleTypes | null {
    try {
      const token = SecureStorage.getToken();
      if (!token) {
        Logger.security("No valid token found for role validation");
        return null;
      }

      // Check if token is expired
      if (TokenUtils.isTokenExpired(token)) {
        Logger.security("Token expired during role validation");
        this.clearRoleCache();
        return null;
      }

      const decoded = TokenUtils.decodeToken(token);
      if (!decoded) {
        Logger.security("Failed to decode token for role validation");
        return null;
      }

      // Extract role from token payload
      const tokenRole = this.extractRoleFromTokenPayload(decoded);

      if (tokenRole) {
        // Cache the verified role with expiry
        this.cacheVerifiedRole(token, tokenRole);
        Logger.security(`Role validated from token: ${tokenRole}`);
        return tokenRole;
      }

      Logger.security("No valid role found in token");
      return null;
    } catch (error) {
      Logger.error("Error validating role from token", error);
      return null;
    }
  }

  /**
   * Extract role from token payload
   */
  private static extractRoleFromTokenPayload(payload: any): RoleTypes | null {
    Logger.dev("JWT Token payload for role extraction:", payload);

    const roleFields = [
      "role",
      "user_role",
      "roles",
      "authorities",
      "permissions",
    ];

    for (const field of roleFields) {
      if (payload[field]) {
        const role = this.normalizeRole(payload[field]);
        if (this.isValidRole(role)) {
          Logger.dev(`Role found in field '${field}': ${role}`);
          return role as RoleTypes;
        }
      }
    }

    // If no role in token, try to derive from user_type or similar
    if (payload.user_type) {
      const role = this.normalizeRole(payload.user_type);
      if (this.isValidRole(role)) {
        Logger.dev(`Role derived from user_type: ${role}`);
        return role as RoleTypes;
      }
    }

    return null;
  }
  /**
   * Normalize role string to match our RoleTypes
   */
  private static normalizeRole(role: string | string[]): string {
    if (Array.isArray(role)) {
      role = role[0]; // Take first role if array
    }

    return role
      .toString()
      .trim()
      .replace(/[-_\s]/g, "_") // Convert hyphens/spaces to underscores
      .replace(/([a-z])([A-Z])/g, "$1_$2") // Convert camelCase to snake_case
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join("_");
  }

  /**
   * Validate if role is one of our defined RoleTypes
   */
  private static isValidRole(role: string): boolean {
    const validRoles: RoleTypes[] = [
      "Super_Admin",
      "LEA_Admin",
      "LEA_Sub_Admin",
      "School_Admin",
      "Teacher",
      "AttandanceCoordinator",
      "Parent",
      "Student",
      "Clerk",
      "Default_Role",
    ];

    return validRoles.includes(role as RoleTypes);
  }

  /**
   * Cache verified role with token-based expiry
   */
  private static cacheVerifiedRole(token: string, role: RoleTypes): void {
    try {
      const decoded = TokenUtils.decodeToken(token);
      if (decoded) {
        const expiryTime = decoded.exp * 1000; // Convert to milliseconds
        this.roleCache.set(token, { role, expires: expiryTime });
      }
    } catch (error) {
      Logger.error("Failed to cache verified role", error);
    }
  }

  /**
   * Clear role cache (on logout, token refresh, etc.)
   */
  static clearRoleCache(): void {
    this.roleCache.clear();
  }

  /**
   * Validate user has specific role
   */
  static hasRole(requiredRole: RoleTypes): boolean {
    const currentRole = this.getRoleFromToken();
    return currentRole === requiredRole;
  }

  /**
   * Validate user has any of the specified roles
   */
  static hasAnyRole(roles: RoleTypes[]): boolean {
    const currentRole = this.getRoleFromToken();
    return currentRole ? roles.includes(currentRole) : false;
  }

  /**
   * Get role hierarchy level for permission comparison
   */
  static getRoleHierarchy(role: RoleTypes): number {
    const hierarchy: Record<RoleTypes, number> = {
      Super_Admin: 100,
      LEA_Admin: 80,
      LEA_Sub_Admin: 70,
      School_Admin: 60,
      AttandanceCoordinator: 50,
      Teacher: 40,
      Clerk: 30,
      Parent: 20,
      Student: 10,
      Default_Role: 0,
    };

    return hierarchy[role] || 0;
  }

  /**
   * Check if current user has higher or equal role level
   */
  static hasMinimumRole(minimumRole: RoleTypes): boolean {
    const currentRole = this.getRoleFromToken();
    if (!currentRole) return false;

    const currentLevel = this.getRoleHierarchy(currentRole);
    const requiredLevel = this.getRoleHierarchy(minimumRole);

    return currentLevel >= requiredLevel;
  }

  /**
   * Secure role retrieval with fallback validation
   * Note: Current backend JWT tokens don't contain role information
   */
  static getSecureRole(): RoleTypes {
    // Primary: Get from token (if backend adds role to JWT in future)
    const tokenRole = this.getRoleFromToken();
    if (tokenRole && tokenRole !== "Default_Role") {
      return tokenRole;
    }

    // Current reality: JWT doesn't contain role, so validate token + use stored role
    const token = SecureStorage.getToken();
    if (!token || TokenUtils.isTokenExpired(token)) {
      Logger.security("No valid token for role validation");
      SecureStorage.clearAll();
      return "Default_Role";
    }

    // Token is valid, check stored role
    const userData = SecureStorage.getUserData();
    const storedRole = userData?.role;

    if (storedRole && this.isValidRole(storedRole)) {
      Logger.dev(`Using stored role with valid token: ${storedRole}`);
      return storedRole as RoleTypes;
    }

    Logger.security("No valid role found, defaulting to Default_Role");
    return "Default_Role";
  }
  /**
   * Validate route access for current user
   */
  static canAccessRoute(route: string): boolean {
    const role = this.getRoleFromToken();
    if (!role || role === "Default_Role") {
      Logger.security(`Route access denied - no valid role for: ${route}`);
      return false;
    }

    // Define route access patterns
    const routeAccess: Record<string, RoleTypes[]> = {
      "/app/dashboard": ["Super_Admin", "LEA_Admin", "School_Admin"],
      "/app/manage-leas": ["Super_Admin"],
      "/app/manage-schools": ["Super_Admin", "LEA_Admin", "School_Admin"],
      "/app/manage-admins": ["LEA_Admin"],
      "/app/reports": [
        "Super_Admin",
        "LEA_Admin",
        "School_Admin",
        "AttandanceCoordinator",
        "Clerk",
      ],
      "/app/assignments": ["Student", "Teacher"],
      "/app/attendance-recovery": ["Parent"],
      "/app/recovery-requests": [
        "Teacher",
        "School_Admin",
        "LEA_Admin",
        "AttandanceCoordinator",
        "Clerk",
      ],
      "/app/school-users": ["School_Admin"],
      // Add more routes as needed
    };

    const allowedRoles = routeAccess[route];
    if (allowedRoles) {
      const hasAccess = allowedRoles.includes(role);
      Logger.security(
        `Route access check - ${route} for role ${role}: ${
          hasAccess ? "ALLOWED" : "DENIED"
        }`
      );
      return hasAccess;
    }

    // Default to allow access if route not specifically restricted
    return true;
  }

  /**
   * Security audit information
   */
  static getSecurityAudit() {
    const token = SecureStorage.getToken();
    const role = this.getRoleFromToken();

    return {
      hasValidToken: !!token,
      tokenExpired: token ? TokenUtils.isTokenExpired(token) : true,
      verifiedRole: role,
      cacheSize: this.roleCache.size,
      securityLevel: role === "Default_Role" ? "LOW" : "HIGH",
      lastValidation: new Date().toISOString(),
    };
  }
}

export default RoleValidator;
